import React, { useState } from 'react';
import { Inbox, Search, GripVertical, ChevronRight, ChevronLeft } from 'lucide-react';
import Badge from '../ui/Badge';

const WALNUT = '#8B4513';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

// Drag payload type — DayCell accepts drops of this type.
export const JOB_DRAG_TYPE = 'application/x-twp-job';

// Ready jobs first (they can go out now), then in production, then on hold;
// oldest sale first within each group.
const STATUS_ORDER = { ready_for_install: 0, in_production: 1, on_hold: 2 };
const soldTime = j => {
  const d = j.soldDate?.toDate ? j.soldDate.toDate() : j.soldDate ? new Date(j.soldDate) : null;
  return d && !isNaN(d) ? d.getTime() : Infinity;
};

const COLLAPSE_KEY = 'twp_calendar_unscheduled_collapsed';
const PAGE_SIZE = 8;

export default function UnscheduledPanel({ jobs = [], onJobClick }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  function toggle() {
    setCollapsed(v => {
      localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      return !v;
    });
  }

  const term = search.trim().toLowerCase();
  const shown = jobs
    .filter(j => !term || [j.customerName, j.contractNumber, j.name, j.address]
      .some(v => String(v || '').toLowerCase().includes(term)))
    .sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || (soldTime(a) - soldTime(b)));

  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const safePage  = Math.min(page, pageCount - 1); // list can shrink as jobs get scheduled
  const pageJobs  = shown.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggle}
        title="Show unscheduled jobs"
        style={{
          alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
          padding: '12px 8px', background: 'var(--bg-tertiary)', border: '1px solid var(--brand-brown-tint-line)',
          borderRadius: '12px', cursor: 'pointer', color: WALNUT, fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={14} strokeWidth={2.5} />
        <Inbox size={16} strokeWidth={2} />
        <span style={{ fontSize: '12px', fontWeight: '800' }}>{jobs.length}</span>
      </button>
    );
  }

  return (
    <div style={{
      flex: '0 0 270px', alignSelf: 'flex-start', position: 'sticky', top: '16px',
      maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-tertiary)', border: '1px solid var(--brand-brown-tint-line)',
      borderRadius: '14px', boxShadow: '0 2px 8px var(--brand-brown-tint-soft)', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px 8px' }}>
        <Inbox size={15} strokeWidth={2} color={WALNUT} />
        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', flex: 1 }}>Unscheduled</span>
        <span style={{ background: `${WALNUT}1A`, color: WALNUT, borderRadius: '999px', padding: '0 8px', fontSize: '11px', fontWeight: '800' }}>{jobs.length}</span>
        <button
          type="button" onClick={toggle} title="Hide panel"
          style={{ display: 'flex', background: 'transparent', border: 'none', cursor: 'pointer', color: STONE, padding: '2px' }}
        >
          <ChevronRight size={15} strokeWidth={2.5} />
        </button>
      </div>

      <div style={{ padding: '0 14px 6px', fontSize: '11px', color: STONE }}>
        Drag a job onto a day to schedule it.
      </div>

      <div style={{ position: 'relative', margin: '4px 14px 10px' }}>
        <Search size={13} strokeWidth={2} color={STONE} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search name or contract #"
          style={{
            width: '100%', padding: '7px 10px 7px 28px', fontSize: '12px',
            border: `1px solid ${STONE}55`, borderRadius: '8px', background: PAPER,
            outline: 'none', fontFamily: 'inherit', color: 'var(--text-primary)',
          }}
        />
      </div>

      <div style={{ overflowY: 'auto', padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {shown.length === 0 ? (
          <div style={{ fontSize: '12px', color: STONE, textAlign: 'center', padding: '20px 0' }}>
            {term ? 'No matches' : 'Everything is scheduled 🎉'}
          </div>
        ) : pageJobs.map(job => (
          <div
            key={job.id}
            draggable
            onDragStart={e => {
              e.dataTransfer.setData(JOB_DRAG_TYPE, job.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onClick={() => onJobClick(job)}
            title="Drag onto a day to schedule · click for details"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: '6px',
              padding: '8px 8px 8px 4px', background: PAPER,
              border: `1px solid ${STONE}33`, borderRadius: '8px',
              cursor: 'grab', userSelect: 'none', transition: 'box-shadow 0.15s, transform 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.boxShadow = `0 3px 10px ${WALNUT}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
          >
            <GripVertical size={14} strokeWidth={2} color={`${STONE}99`} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {job.contractNumber && <span style={{ color: WALNUT, marginRight: '5px' }}>#{job.contractNumber}</span>}
                {job.customerName || job.name || 'Unnamed'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap', fontSize: '10px', color: STONE }}>
                <Badge status={job.status} style={{ fontSize: '9px', padding: '1px 6px' }}>{job.status.replace(/_/g, ' ')}</Badge>
                {job.windowCount > 0 && <span>{job.windowCount} win</span>}
                {job.doorCount > 0 && <span>{job.doorCount} door</span>}
                {job.durationDays > 1 && <span>{job.durationDays} days</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',
          padding: '8px 12px', borderTop: '1px solid var(--brand-brown-tint-line)',
          fontSize: '11px', color: STONE,
        }}>
          <button type="button" style={pagerBtn(safePage === 0)} disabled={safePage === 0} onClick={() => setPage(safePage - 1)} aria-label="Previous page">
            <ChevronLeft size={14} strokeWidth={2.5} />
          </button>
          <span>
            <strong style={{ color: 'var(--text-primary)' }}>{safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, shown.length)}</strong> of {shown.length}
            <span style={{ marginLeft: '6px' }}>· page {safePage + 1}/{pageCount}</span>
          </span>
          <button type="button" style={pagerBtn(safePage >= pageCount - 1)} disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} aria-label="Next page">
            <ChevronRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}

function pagerBtn(disabled) {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '28px', height: '26px', borderRadius: '6px',
    background: disabled ? 'transparent' : `${WALNUT}14`,
    border: 'none', color: disabled ? `${STONE}66` : WALNUT,
    cursor: disabled ? 'default' : 'pointer',
  };
}
